import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'sonner';
import { Heart, Gift, Sparkles, CreditCard, Shield, Mail, Repeat, ChevronRight, TrendingUp, Users, Target, Lock, Globe, Clock } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const DonatePage = () => {
  const { t, i18n } = useTranslation();
  const [recurring, setRecurring] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [hoveredAmount, setHoveredAmount] = useState(null);

  const predefinedAmounts = [10, 25, 50, 100, 250, 500];
  const annualGoal = 100000;
  const raised = 45000;

  // One-time donation links
  const oneTimeLinks = {
    10: 'https://donate.stripe.com/bJe14ndaN1sGaBSdLcco00c',
    25: 'https://donate.stripe.com/7sY8wP9YB4ESfWc22uco00d',
    50: 'https://donate.stripe.com/4gM8wPeeR8V87pG7mOco00e',
    100: 'https://donate.stripe.com/cNi4gz4EhfjwbFW9uWco00f',
    250: 'https://donate.stripe.com/6oU5kD6Mp2wKeS822uco00g',
    500: 'https://donate.stripe.com/aFa3cv0o16N08tKgXoco00h',
  };

  // Monthly subscription links
  const monthlyLinks = {
    10: 'https://donate.stripe.com/14AdR98Ux0oC5hy7mOco00o',
    25: 'https://donate.stripe.com/3cIcN5fiVefscK06iKco00n',
    50: 'https://donate.stripe.com/cNieVd7Qt7R4cK0ePgco00m',
    100: 'https://donate.stripe.com/bJe3cvgmZ8V84dugXoco00l',
    250: 'https://donate.stripe.com/dRm14n1s53AO39qcH8co00k',
    500: 'https://donate.stripe.com/28E8wP3Adfjwh0g4aCco00j',
  };

  // Custom donation link (only for one-time)
  const customDonationLink = 'https://donate.stripe.com/eVq6oHc6J3AO8tK8qSco00i';

  const handleDonate = (donationAmount) => {
    if (!donationAmount || donationAmount <= 0) {
      toast.error(i18n.language === 'bg' ? 'Моля въведете валидна сума' : 'Please enter a valid amount');
      return;
    }

    // Check if it's custom donation (only for one-time)
    if (donationAmount === 'custom') {
      window.location.href = customDonationLink;
      return;
    }

    const isValidAmount = predefinedAmounts.includes(Number(donationAmount));
    
    if (!isValidAmount) {
      toast.error(i18n.language === 'bg' 
        ? 'Моля, изберете една от предварително зададените суми за дарение' 
        : 'Please select one of the predefined donation amounts');
      return;
    }

    const links = recurring ? monthlyLinks : oneTimeLinks;
    const stripeLink = links[donationAmount];

    if (stripeLink) {
      window.location.href = stripeLink;
    }
  };

  const handleRecurringToggle = () => {
    setRecurring(!recurring);
    // Clear selected amount when toggling to monthly (since custom doesn't exist for monthly)
    if (!recurring && selectedAmount === 'custom') {
      setSelectedAmount(null);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen py-32 bg-gradient-to-br from-background via-background to-accent/5">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent/5 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-accent/3 blur-3xl"></div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
        {/* Hero Section with Animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
            className="inline-block mb-6"
          >
            <Heart className="w-16 h-16 text-accent fill-accent/20" />
          </motion.div>
          <h1 className="font-heading text-5xl md:text-7xl font-bold bg-gradient-to-r from-foreground to-accent bg-clip-text text-transparent mb-6">
            {t('donate.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('donate.subtitle')}
          </p>
        </motion.div>

        {/* Progress Section with Impact Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-card to-card/80 backdrop-blur-sm border border-accent/20 p-8 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div className="text-center md:text-left">
              <p className="text-sm text-muted-foreground mb-1 flex items-center justify-center md:justify-start gap-2">
                <TrendingUp className="w-4 h-4" />
                {i18n.language === 'bg' ? 'Събрано до момента' : 'Raised so far'}
              </p>
              <p className="font-heading text-4xl font-bold bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                €{raised.toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1 flex items-center justify-center gap-2">
                <Target className="w-4 h-4" />
                {i18n.language === 'bg' ? 'Годишна цел' : 'Annual Goal'}
              </p>
              <p className="font-heading text-3xl font-bold text-foreground">
                €{annualGoal.toLocaleString()}
              </p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-muted-foreground mb-1 flex items-center justify-center md:justify-end gap-2">
                <Users className="w-4 h-4" />
                {i18n.language === 'bg' ? 'Подкрепени деца' : 'Children Supported'}
              </p>
              <p className="font-heading text-3xl font-bold text-foreground">
                {Math.floor(raised / 400)}+
              </p>
            </div>
          </div>
          <Progress value={(raised / annualGoal) * 100} className="h-3" />
          <p className="text-sm text-muted-foreground mt-3 text-center">
            {Math.round((raised / annualGoal) * 100)}% {i18n.language === 'bg' ? 'от годишната цел е постигната' : 'of annual goal achieved'}
          </p>
        </motion.div>

        {/* Main Donation Card */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-gradient-to-br from-card to-card/95 backdrop-blur-sm border border-accent/20 p-8 mb-8"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold mb-2">
              {i18n.language === 'bg' ? 'Изберете сума на дарение' : 'Choose Donation Amount'}
            </h2>
            <p className="text-muted-foreground">
              {i18n.language === 'bg' 
                ? 'Всяка сума има значение и помага за промяна' 
                : 'Every amount matters and helps make a difference'}
            </p>
          </motion.div>
          
          {/* Amount Buttons */}
          <motion.div variants={itemVariants} className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-8">
            {predefinedAmounts.map((amt) => (
              <motion.button
                key={amt}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedAmount(amt)}
                onMouseEnter={() => setHoveredAmount(amt)}
                onMouseLeave={() => setHoveredAmount(null)}
                className={`relative p-4 font-bold text-lg transition-all duration-300 overflow-hidden group ${
                  selectedAmount === amt
                    ? 'bg-gradient-to-r from-accent to-primary text-white'
                    : 'bg-secondary/50 border border-border hover:border-accent/50 text-foreground'
                }`}
              >
                <span className="relative z-10">€{amt}</span>
                {hoveredAmount === amt && (
                  <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 0.6, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  />
                )}
                {selectedAmount === amt && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-1 right-1"
                  >
                    <Sparkles className="w-4 h-4 text-yellow-300" />
                  </motion.div>
                )}
              </motion.button>
            ))}
          </motion.div>

          {/* Custom Donation Button - shows only when NOT recurring (one-time only) */}
          <AnimatePresence>
            {!recurring && (
              <motion.div
                variants={itemVariants}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <button
                  onClick={() => setSelectedAmount('custom')}
                  className={`w-full p-4 font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                    selectedAmount === 'custom'
                      ? 'bg-gradient-to-r from-accent to-primary text-white'
                      : 'bg-secondary/50 border border-border hover:border-accent/50 text-foreground'
                  }`}
                >
                  <Gift className="w-5 h-5" />
                  {i18n.language === 'bg' ? 'Друга сума' : 'Other amount'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Recurring Option with Rounded Toggle */}
          <motion.div variants={itemVariants} className="mt-6 flex items-center justify-between p-4 bg-secondary/30">
            <div className="flex items-center gap-3">
              <Repeat className={`w-5 h-5 transition-colors ${recurring ? 'text-accent' : 'text-muted-foreground'}`} />
              <span className="text-foreground">
                {i18n.language === 'bg' ? 'Месечно дарение' : 'Monthly donation'}
              </span>
            </div>
            <button
              onClick={handleRecurringToggle}
              className={`relative w-14 h-7 transition-all duration-300 rounded-full ${
                recurring ? 'bg-accent' : 'bg-border'
              }`}
            >
              <motion.div
                animate={{ x: recurring ? 28 : 2 }}
                className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
              />
            </button>
          </motion.div>

          {/* Warning message for monthly recurring */}
          <AnimatePresence>
            {recurring && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-4 p-3 bg-accent/10 border border-accent/20 text-center"
              >
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'bg' 
                    ? '📅 Месечните дарения са само с фиксирани суми (10, 25, 50, 100, 250, 500 EUR)'
                    : '📅 Monthly donations are only available with fixed amounts (10, 25, 50, 100, 250, 500 EUR)'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stripe Payment Info */}
          <motion.div variants={itemVariants} className="mt-6 p-4 bg-accent/5 border border-accent/20">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-accent" />
              <span className="font-semibold text-foreground">
                {i18n.language === 'bg' ? 'Плащанията се обработват от Stripe' : 'Payments are processed by Stripe'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>{i18n.language === 'bg' ? 'Напълно защитено' : 'Fully secure'}</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span>Visa, Mastercard, AMEX</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                <span>{i18n.language === 'bg' ? 'Международни плащания' : 'International payments'}</span>
              </div>
            </div>
          </motion.div>

          {/* Donate Button */}
          <motion.div variants={itemVariants} className="mt-8">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                if (selectedAmount) {
                  handleDonate(selectedAmount);
                }
              }}
              disabled={!selectedAmount}
              className="w-full bg-gradient-to-r from-accent to-primary text-white font-bold uppercase tracking-widest px-8 py-6 text-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 group"
            >
              <Heart className="w-6 h-6 group-hover:animate-pulse" />
              {i18n.language === 'bg' ? 'Дари сега' : 'Donate Now'}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
        >
          <div className="text-center p-4 bg-card/50 backdrop-blur-sm border border-accent/10">
            <Shield className="w-8 h-8 text-accent mx-auto mb-2" />
            <p className="font-semibold">{i18n.language === 'bg' ? 'Сигурно плащане' : 'Secure payment'}</p>
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'bg' ? 'Защитена от Stripe технология' : 'Protected by Stripe technology'}
            </p>
          </div>
          <div className="text-center p-4 bg-card/50 backdrop-blur-sm border border-accent/10">
            <Mail className="w-8 h-8 text-accent mx-auto mb-2" />
            <p className="font-semibold">{i18n.language === 'bg' ? 'Потвърждение' : 'Confirmation'}</p>
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'bg' ? 'Получавате имейл от Stripe' : 'You receive an email from Stripe'}
            </p>
          </div>
          <div className="text-center p-4 bg-card/50 backdrop-blur-sm border border-accent/10">
            <Clock className="w-8 h-8 text-accent mx-auto mb-2" />
            <p className="font-semibold">{i18n.language === 'bg' ? 'Бързо и лесно' : 'Fast and easy'}</p>
            <p className="text-sm text-muted-foreground">
              {i18n.language === 'bg' ? 'Само няколко клика' : 'Just a few clicks'}
            </p>
          </div>
        </motion.div>

        {/* Motivational Quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-lg italic text-muted-foreground">
            "⭐ {i18n.language === 'bg' 
              ? 'Всяко дарение е инвестиция в бъдещето на нашите деца' 
              : 'Every donation is an investment in our children\'s future'}"
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default DonatePage;